import React, { useState } from 'react';
import { 
  BookOpen, 
  Plus, 
  Trash2, 
  Check,
  ChevronDown,
  ChevronUp,
  Edit3,
  Copy,
  Link
} from 'lucide-react';
import { FewShotExample } from '../types/api';
import { cn } from '../lib/utils';

interface FewShotManagerProps {
  examples: FewShotExample[];
  onExamplesChange: (examples: FewShotExample[]) => void;
  enableChainOfThought: boolean;
  onChainOfThoughtChange: (enabled: boolean) => void;
  isLoading?: boolean;
}

// Default few-shot examples
const DEFAULT_EXAMPLES: FewShotExample[] = [
  {
    profile_summary: "Donna, 25 anni, laureata, lavora nel settore tech. Politicamente orientata a sinistra (3/10), molto interessata alla politica (8/10). Valuta negativamente il governo attuale (2/10). Ha fiducia nell'UE (7/10).",
    reasoning: "Considerando il mio profilo: sono giovane e istruita, con un forte interesse per la politica. La mia collocazione a sinistra mi porta a essere critica verso le istituzioni tradizionali. Tuttavia, il mio scetticismo verso i partiti nasce dalla percezione che non rappresentino adeguatamente le mie istanze progressiste.",
    answer: "no"
  },
  {
    profile_summary: "Uomo, 32 anni, diploma superiore, impiegato. Politicamente moderato (5/10), interesse medio per la politica (5/10). Valutazione neutra del governo (5/10). Fiducia media nell'UE (5/10).",
    reasoning: "Il mio profilo è quello di una persona moderata che cerca stabilità. Pur non essendo entusiasta dei partiti politici, riconosco il loro ruolo fondamentale nella democrazia. La mia posizione centrista mi porta a vedere sia i difetti che i meriti del sistema partitico.",
    answer: "si"
  },
  {
    profile_summary: "Donna, 19 anni, studentessa universitaria. Orientamento politico a destra (7/10), poco interessata alla politica (3/10). Non ha votato alle ultime elezioni. Sfiducia verso le istituzioni europee (3/10).",
    reasoning: "Anche se mi colloco a destra politicamente, il mio scarso interesse per la politica riflette una disillusione generale. I partiti mi sembrano lontani dai problemi reali dei giovani. La mia decisione di non votare dimostra questa sfiducia.",
    answer: "no"
  }
];

const FewShotManager: React.FC<FewShotManagerProps> = ({
  examples,
  onExamplesChange,
  enableChainOfThought,
  onChainOfThoughtChange,
  isLoading = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const addExample = () => {
    const newExample: FewShotExample = {
      profile_summary: '',
      reasoning: enableChainOfThought ? '' : undefined,
      answer: 'si'
    };
    onExamplesChange([...examples, newExample]);
    setEditingIndex(examples.length);
  };

  const removeExample = (index: number) => {
    const newExamples = examples.filter((_, i) => i !== index);
    onExamplesChange(newExamples);
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const updateExample = (index: number, field: keyof FewShotExample, value: string) => {
    const newExamples = [...examples];
    newExamples[index] = { ...newExamples[index], [field]: value };
    onExamplesChange(newExamples);
  };

  const loadDefaultExamples = () => {
    const adjustedExamples = DEFAULT_EXAMPLES.map(ex => ({
      ...ex,
      reasoning: enableChainOfThought ? ex.reasoning : undefined
    }));
    onExamplesChange(adjustedExamples);
  };

  const copyExample = (index: number) => {
    const example = examples[index];
    navigator.clipboard.writeText(JSON.stringify(example, null, 2));
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Chain of Thought Toggle */}
      <div className="flex items-center justify-between p-3 border rounded-md">
        <div className="flex items-center space-x-2">
          <Link className="h-4 w-4 text-muted-foreground" />
          <div>
            <span className="text-sm font-medium">Chain-of-Thought</span>
            <p className="text-xs text-muted-foreground">Step-by-step reasoning</p>
          </div>
        </div>
        
        <button
          onClick={() => onChainOfThoughtChange(!enableChainOfThought)}
          disabled={isLoading}
          className={cn(
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
            enableChainOfThought ? "bg-foreground" : "bg-muted",
            isLoading && "opacity-50 cursor-not-allowed"
          )}
        >
          <span
            className={cn(
              "inline-block h-3.5 w-3.5 transform rounded-full bg-background transition-transform duration-200",
              enableChainOfThought ? "translate-x-5" : "translate-x-1"
            )}
          />
        </button>
      </div>

      {enableChainOfThought && (
        <p className="text-xs text-muted-foreground px-3">
          The AI will explain its reasoning process before giving a final answer.
        </p>
      )}

      {/* Few-Shot Examples Section */}
      <div className="rounded-md border bg-card">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={isLoading}
          className="w-full p-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            <div className="text-left">
              <h3 className="text-sm font-medium">Few-Shot Examples</h3>
              <p className="text-xs text-muted-foreground">
                {examples.length === 0 
                  ? 'Add examples to guide predictions' 
                  : `${examples.length} example${examples.length > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {examples.length > 0 && (
              <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                {examples.length}
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="p-3 border-t space-y-3 animate-in fade-in duration-200">
            {/* Quick Actions */}
            <div className="flex items-center justify-between">
              <button
                onClick={loadDefaultExamples}
                disabled={isLoading}
                className={cn(
                  "inline-flex items-center space-x-1.5 px-2.5 py-1.5 rounded text-xs font-medium",
                  "bg-muted text-muted-foreground hover:text-foreground",
                  "transition-colors duration-150",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                <span>Load Defaults</span>
              </button>
              
              <button
                onClick={addExample}
                disabled={isLoading}
                className={cn(
                  "inline-flex items-center space-x-1 px-2.5 py-1.5 rounded text-xs font-medium",
                  "bg-foreground text-background hover:bg-foreground/90",
                  "transition-colors duration-150",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                <Plus className="h-3 w-3" />
                <span>Add</span>
              </button>
            </div>

            {/* Examples List */}
            {examples.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <BookOpen className="h-6 w-6 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No examples yet</p>
                <p className="text-xs mt-1">Add examples to improve accuracy</p>
              </div>
            ) : (
              <div className="space-y-2">
                {examples.map((example, index) => (
                  <div
                    key={index}
                    className={cn(
                      "rounded-md border transition-all duration-150",
                      editingIndex === index 
                        ? "border-foreground/30 bg-muted/30" 
                        : "bg-card hover:bg-muted/20"
                    )}
                  >
                    <div className="p-2.5">
                      {/* Example Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-muted-foreground">
                            #{index + 1}
                          </span>
                          <span className={cn(
                            "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium",
                            example.answer === 'si' 
                              ? "bg-muted text-foreground" 
                              : "bg-muted text-foreground"
                          )}>
                            {example.answer === 'si' ? 'Yes' : 'No'}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-0.5">
                          <button
                            onClick={() => copyExample(index)}
                            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                            title="Copy"
                          >
                            {copiedIndex === index ? (
                              <Check className="h-3.5 w-3.5" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => removeExample(index)}
                            disabled={isLoading}
                            className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Example Content */}
                      {editingIndex === index ? (
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">
                              Profile
                            </label>
                            <textarea
                              value={example.profile_summary}
                              onChange={(e) => updateExample(index, 'profile_summary', e.target.value)}
                              placeholder="Describe the persona..."
                              rows={2}
                              className="w-full px-2.5 py-1.5 text-sm border border-input bg-background rounded focus:outline-none focus:ring-1 focus:ring-ring transition-colors resize-none"
                              disabled={isLoading}
                            />
                          </div>
                          
                          {enableChainOfThought && (
                            <div>
                              <label className="text-xs text-muted-foreground block mb-1">
                                Reasoning
                              </label>
                              <textarea
                                value={example.reasoning || ''}
                                onChange={(e) => updateExample(index, 'reasoning', e.target.value)}
                                placeholder="Explain the reasoning..."
                                rows={2}
                                className="w-full px-2.5 py-1.5 text-sm border border-input bg-background rounded focus:outline-none focus:ring-1 focus:ring-ring transition-colors resize-none"
                                disabled={isLoading}
                              />
                            </div>
                          )}
                          
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">
                              Answer
                            </label>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => updateExample(index, 'answer', 'si')}
                                className={cn(
                                  "flex-1 py-1.5 rounded text-sm font-medium transition-all duration-150",
                                  example.answer === 'si'
                                    ? "bg-foreground text-background"
                                    : "bg-muted text-muted-foreground hover:text-foreground"
                                )}
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => updateExample(index, 'answer', 'no')}
                                className={cn(
                                  "flex-1 py-1.5 rounded text-sm font-medium transition-all duration-150",
                                  example.answer === 'no'
                                    ? "bg-foreground text-background"
                                    : "bg-muted text-muted-foreground hover:text-foreground"
                                )}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-sm line-clamp-2">
                            {example.profile_summary || <span className="text-muted-foreground italic">No description</span>}
                          </p>
                          {enableChainOfThought && example.reasoning && (
                            <p className="text-xs text-muted-foreground line-clamp-1 pl-2 border-l border-muted-foreground/30">
                              {example.reasoning}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Help Text */}
            <p className="text-xs text-muted-foreground">
              Include diverse examples with balanced yes/no answers for best results.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FewShotManager;
